import React, { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import FormProcessingSpinner from "./FormProcessingSpinner";
import { getAuthUserToken } from "../../helpers/authToken";
import { toast, ToastContainer } from "react-toastify";
import { TOAST_SETTINGS } from "../../utils/siteSettings";
import Text from "./Text";
import http from "../../helpers/http";
import { doObjToFormData } from "../../helpers/helpers";
import { useNavigate } from "react-router-dom";
import {
  ExperienceFields,
  ProjectFields,
  VolunteeringFields,
  PublicationFields,
  CertificationFields,
  AwardFields,
  HobbyFields,
  RightToWorkFields,
} from "./CvSectionFields";

function EditCvPopup({ handleClose, cvDetails, cv_id, onSuccess }) {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: cvDetails,
  });
  const navigate = useNavigate();
  const [isFormProcessing, setisFormProcessing] = useState(false);
  const {
    fields: skillFields,
    append: addSkill,
    remove: removeSkill,
  } = useFieldArray({
    control,
    name: "key_skills",
    rules: { required: "At least one skill is required" },
  });

  const {
    fields: expFields,
    append: addExp,
    remove: removeExp,
  } = useFieldArray({ control, name: "work_experience" });

  const {
    fields: eduFields,
    append: addEdu,
    remove: removeEdu,
  } = useFieldArray({ control, name: "education" });

  const {
    fields: projectFields,
    append: addProject,
    remove: removeProject,
  } = useFieldArray({ control, name: "projects" });

  const {
    fields: volunteeringFields,
    append: addVolunteer,
    remove: removeVolunteer,
  } = useFieldArray({ control, name: "volunteering" });

  const {
    fields: publicationFields,
    append: addPublication,
    remove: removePublication,
  } = useFieldArray({ control, name: "publications" });

  const {
    fields: certificationFields,
    append: addCertification,
    remove: removeCertification,
  } = useFieldArray({ control, name: "certifications" });

  const {
    fields: awardFields,
    append: addAward,
    remove: removeAward,
  } = useFieldArray({ control, name: "awards" });

  const {
    fields: hobbyFields,
    append: addHobby,
    remove: removeHobby,
  } = useFieldArray({ control, name: "hobbies" });

  const {
    fields: rightFields,
    append: addRight,
    remove: removeRight,
  } = useFieldArray({ control, name: "right_to_work" });

  const onSubmit = (data) => {
    // Filter out empty skills, experiences, and education
    const filteredData = {
      ...data,
      key_skills: data.key_skills.filter((skill) => skill.trim() !== ""),
      work_experience: data.work_experience.filter(
        (exp) => exp.job_title?.trim() !== "" || exp.company?.trim() !== ""
      ),
      education: data.education.filter(
        (edu) => edu.degree?.trim() !== "" || edu.institution?.trim() !== ""
      ),

      projects: data.projects.filter(
        (p) => p.project_name?.trim() || p.organization?.trim()
      ),
      volunteering: data.volunteering.filter(
        (v) => v.role_title?.trim() || v.organization?.trim()
      ),
      publications: data.publications.filter((p) => p.title?.trim()),
      certifications: data.certifications.filter((c) => c.name?.trim()),
      awards: data.awards.filter((a) => a.name?.trim()),
      hobbies: data.hobbies.filter((h) => h.title?.trim()),
      right_to_work: data.right_to_work.filter((r) => r.title?.trim()),
    };

    const formData = {
      cv_details: filteredData,
      token: getAuthUserToken(),
      cv_id: cv_id,
    };
    setisFormProcessing(true);
    http
      .post("user/update-cv", doObjToFormData(formData))
      .then(({ data }) => {
        if (data.status) {
          toast.success("CV Updated Successfully");
          setTimeout(() => {
            setisFormProcessing(false);
            if (onSuccess) {
              onSuccess(); // call parent’s function
            }
            // window.location.reload();
            // navigate("/my-cvs");
          }, 2000);
        } else {
          if (data.validationErrors) {
            toast.error(
              <Text string={data.validationErrors} parse={true} />,
              TOAST_SETTINGS
            );
          }
        }
      })
      .catch((error) => {
        console.log("Update CV Error:", error);
        setisFormProcessing(false);
      });
  };

  return (
    <div
      className="popup lg show fade d-block u_cv_edit"
      tabIndex="-1"
      role="dialog"
    >
      <ToastContainer />
      <div className="table_dv">
        <div className="table_cell">
          <div className="_inner" role="document">
            <h3 className="modal-title fw-bold">Edit Your CV</h3>
            <button
              type="button"
              className="x_btn"
              onClick={handleClose}
            ></button>

            <div className="modal-body">
              <form onSubmit={handleSubmit(onSubmit)} className="row g-3">
                {/* Personal Info */}
                <div className="col-md-6">
                  <input
                    {...register("name", { required: "Full Name is required" })}
                    className="input"
                    placeholder="Full Name *"
                  />
                  {errors.name && (
                    <span className="text-danger small">
                      {errors.name.message}
                    </span>
                  )}
                </div>

                <div className="col-md-6">
                  <input
                    {...register("current_title", {
                      required: "Current Title is required",
                    })}
                    className="input"
                    placeholder="Current Title *"
                  />
                  {errors.current_title && (
                    <span className="text-danger small">
                      {errors.current_title.message}
                    </span>
                  )}
                </div>

                <div className="col-md-6">
                  <input
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: "Invalid email address",
                      },
                    })}
                    className="input"
                    placeholder="Email *"
                  />
                  {errors.email && (
                    <span className="text-danger small">
                      {errors.email.message}
                    </span>
                  )}
                </div>

                <div className="col-md-6">
                  <input
                    {...register("phone", { required: "Phone is required" })}
                    className="input"
                    placeholder="Phone *"
                  />
                  {errors.phone && (
                    <span className="text-danger small">
                      {errors.phone.message}
                    </span>
                  )}
                </div>

                <div className="col-12">
                  <input
                    {...register("location", {
                      required: "Location is required",
                    })}
                    className="input"
                    placeholder="Location *"
                  />
                  {errors.location && (
                    <span className="text-danger small">
                      {errors.location.message}
                    </span>
                  )}
                </div>

                <div className="col-12 u_inc_height">
                  <textarea
                    {...register("professional_summary", {
                      required: "Professional Summary is required",
                    })}
                    className="input"
                    rows="4"
                    placeholder="Professional Summary *"
                  />
                  {errors.professional_summary && (
                    <span className="text-danger small">
                      {errors.professional_summary.message}
                    </span>
                  )}
                </div>

                {/* Key Skills */}
                <h5 className=" fw-bold u_edit_h5">Key Skills *</h5>
                {skillFields.map((field, index) => (
                  <div
                    key={field.id}
                    className="d-flex gap-2 mb-2 align-items-center u_edit_cross"
                  >
                    <div className="flex-grow-1">
                      <input
                        {...register(`key_skills.${index}`, {
                          required: "Skill is required",
                        })}
                        className="input"
                        placeholder={`Skill ${index + 1} *`}
                      />
                      {errors.key_skills?.[index] && (
                        <span className="text-danger small">
                          {errors.key_skills[index].message}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-danger btn-lg"
                      onClick={() => removeSkill(index)}
                      disabled={skillFields.length <= 1}
                    >
                      X
                    </button>
                  </div>
                ))}
                <div className=" u_edit_btn">
                  <button
                    type="button"
                    className="site_btn blank blank_color_border"
                    onClick={() => addSkill("")}
                  >
                    + Add Skill
                  </button>
                </div>

                {/* Work Experience */}
                <h5 className="fw-bold u_edit_h5">Work Experience</h5>

                {expFields.map((field, index) => (
                  <ExperienceFields
                    key={field.id}
                    control={control}
                    register={register}
                    errors={errors}
                    field={field}
                    index={index}
                    removeExp={removeExp}
                  />
                ))}

                <div className=" u_edit_btn">
                  <button
                    type="button"
                    className="site_btn blank blank_color_border"
                    onClick={() => addExp({})}
                  >
                    + Add Experience
                  </button>
                </div>

                {/* Education */}
                <h5 className=" fw-bold u_edit_h5">Education</h5>
                {eduFields.map((field, index) => (
                  <div className="_uj_space">
                    <div
                      key={field.id}
                      className="border rounded p-3 mb-3 u_edit_relative"
                    >
                      <div className="row g-2">
                        <div className="col-md-6">
                          <input
                            {...register(`education.${index}.degree`, {
                              required: "Degree is required",
                            })}
                            className="input"
                            placeholder="Degree *"
                          />
                          {errors.education?.[index]?.degree && (
                            <span className="text-danger small">
                              {errors.education[index].degree.message}
                            </span>
                          )}
                        </div>
                        <div className="col-md-6">
                          <input
                            {...register(`education.${index}.institution`, {
                              required: "Institution is required",
                            })}
                            className="input"
                            placeholder="Institution *"
                          />
                          {errors.education?.[index]?.institution && (
                            <span className="text-danger small">
                              {errors.education[index].institution.message}
                            </span>
                          )}
                        </div>
                        <div className="col-md-6">
                          <input
                            {...register(`education.${index}.location`, {
                              required: "Location is required",
                            })}
                            className="input"
                            placeholder="Location *"
                          />
                          {errors.education?.[index]?.location && (
                            <span className="text-danger small">
                              {errors.education[index].location.message}
                            </span>
                          )}
                        </div>
                        <div className="col-md-3">
                          <input
                            {...register(`education.${index}.start_year`, {
                              required: "Start Year is required",
                            })}
                            className="input"
                            placeholder="Start Year *"
                          />
                          {errors.education?.[index]?.start_year && (
                            <span className="text-danger small">
                              {errors.education[index].start_year.message}
                            </span>
                          )}
                        </div>
                        <div className="col-md-3">
                          <input
                            {...register(`education.${index}.end_year`, {
                              required: "End Year is required",
                            })}
                            className="input"
                            placeholder="End Year *"
                          />
                          {errors.education?.[index]?.end_year && (
                            <span className="text-danger small">
                              {errors.education[index].end_year.message}
                            </span>
                          )}
                        </div>
                        <div className="col-md-3">
                          <input
                            {...register(`education.${index}.degree_result`)}
                            className="input"
                            placeholder="Degree Result *"
                          />
                        </div>
                        <div className="col-md-9">
                          <input
                            {...register(`education.${index}.key_modules`)}
                            className="input"
                            placeholder="Key Modules *"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-danger btn-md mt-2 btn-danger1"
                        onClick={() => removeEdu(index)}
                      >
                        x
                      </button>
                    </div>
                  </div>
                ))}
                <div className="u_edit_btn">
                  <button
                    type="button"
                    className="site_btn blank blank_color_border"
                    onClick={() => addEdu({})}
                  >
                    + Add Education
                  </button>
                </div>

                {/* Projects Section */}
                <h5 className=" fw-bold u_edit_h5">Projects</h5>
                {projectFields.map((field, index) => (
                  <ProjectFields
                    key={field.id}
                    control={control}
                    register={register}
                    errors={errors}
                    field={field}
                    index={index}
                    removeProject={removeProject}
                  />
                ))}
                <div className="u_edit_btn">
                  <button
                    type="button"
                    className="site_btn blank blank_color_border"
                    onClick={() => addProject({})}
                  >
                    + Add Project
                  </button>
                </div>

                {/* Volunteering & Leadership Section */}
                <h5 className="fw-bold u_edit_h5">Volunteering & Leadership</h5>
                {volunteeringFields.map((field, index) => (
                  <VolunteeringFields
                    key={field.id}
                    control={control}
                    register={register}
                    errors={errors}
                    field={field}
                    index={index}
                    removeVolunteer={removeVolunteer}
                  />
                ))}
                <div className="u_edit_btn">
                  <button
                    type="button"
                    className="site_btn blank blank_color_border"
                    onClick={() => addVolunteer({})}
                  >
                    + Add Volunteering & Leadership
                  </button>
                </div>

                {/* Publications Section */}
                <h5 className="fw-bold u_edit_h5">Publications</h5>
                {publicationFields.map((field, index) => (
                  <PublicationFields
                    key={field.id}
                    register={register}
                    errors={errors}
                    field={field}
                    index={index}
                    removePublication={removePublication}
                  />
                ))}
                <div className="u_edit_btn">
                  <button
                    type="button"
                    className="site_btn blank blank_color_border"
                    onClick={() => addPublication({})}
                  >
                    + Add Publication
                  </button>
                </div>

                {/* Certifications Section */}
                <h5 className="fw-bold u_edit_h5">Certifications</h5>
                {certificationFields.map((field, index) => (
                  <CertificationFields
                    key={field.id}
                    register={register}
                    errors={errors}
                    field={field}
                    index={index}
                    removeCertification={removeCertification}
                  />
                ))}
                <div className="u_edit_btn">
                  <button
                    type="button"
                    className="site_btn blank blank_color_border"
                    onClick={() => addCertification({})}
                  >
                    + Add Certification
                  </button>
                </div>

                {/* Awards & Scholarships Section */}
                <h5 className=" fw-bold u_edit_h5">Awards & Scholarships</h5>
                {awardFields.map((field, index) => (
                  <AwardFields
                    key={field.id}
                    register={register}
                    errors={errors}
                    field={field}
                    index={index}
                    removeAward={removeAward}
                  />
                ))}
                <div className="u_edit_btn">
                  <button
                    type="button"
                    className="site_btn blank blank_color_border"
                    onClick={() => addAward({})}
                  >
                    + Add Awards & Scholarships
                  </button>
                </div>

                {/* Hobbies & Interests Section */}
                <h5 className=" fw-bold u_edit_h5">Hobbies & Interests</h5>
                {hobbyFields.map((field, index) => (
                  <HobbyFields
                    key={field.id}
                    register={register}
                    errors={errors}
                    field={field}
                    index={index}
                    removeHobby={removeHobby}
                  />
                ))}
                <div className="u_edit_btn">
                  <button
                    type="button"
                    className="site_btn blank blank_color_border"
                    onClick={() => addHobby({})}
                  >
                    + Add Hobby or Interest
                  </button>
                </div>

                {/* Right to Work Section */}
                <h5 className="fw-bold u_edit_h5">Right to Work</h5>
                {rightFields.map((field, index) => (
                  <RightToWorkFields
                    key={field.id}
                    register={register}
                    errors={errors}
                    field={field}
                    index={index}
                    removeRight={removeRight}
                  />
                ))}
                <div className="u_edit_btn">
                  <button
                    type="button"
                    className="site_btn blank blank_color_border"
                    onClick={() => addRight({})}
                  >
                    + Add Right to Work
                  </button>
                </div>

                {/* Save Button */}
                <div className="col-12 mt-4 d-flex justify-content-center">
                  <button
                    type="submit"
                    className="site_btn d-flex align-items-center"
                    disabled={isFormProcessing}
                    style={{ minWidth: "120px" }}
                  >
                    Save
                    <FormProcessingSpinner
                      isFormProcessing={isFormProcessing}
                    />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditCvPopup;
